interface CartItem {
  id: string
  name: string
  sku: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface CartState {
  cart: CartItem[]
  selectedCustomer: Customer | null
  paymentMethod: 'cash' | 'card' | 'upi' | 'split'
  amountReceived: string
  splitPayments: { cash: string; card: string; upi: string }
  orderDiscount?: { type: 'fixed' | 'percentage'; value: number } | null
  taxRate: number
  timestamp: number
  id: string
}

interface SuspendedSale extends CartState {
  suspendedAt: number
  suspendedBy?: string
  note?: string
}

interface CompletedSale {
  id: string
  billNumber: string
  cart: CartItem[]
  selectedCustomer: Customer | null
  paymentMethod: 'cash' | 'card' | 'upi' | 'split'
  amountReceived: number
  splitPayments?: { cash: string; card: string; upi: string }
  orderDiscount?: { type: 'fixed' | 'percentage'; value: number } | null
  taxRate: number
  subtotal: number
  taxAmount: number
  totalAmount: number
  change: number
  completedAt: number
  completedBy?: string
}

class CartPersistenceService {
  private readonly ACTIVE_CART_KEY = 'pos_active_cart'
  private readonly SUSPENDED_SALES_KEY = 'pos_suspended_sales'
  private readonly RECENT_SALES_KEY = 'pos_recent_sales'
  private readonly AUTO_SAVE_INTERVAL = 5000 // 5 seconds
  private readonly MAX_RECENT_SALES = 50 // Keep last 50 sales

  private autoSaveTimer: NodeJS.Timeout | null = null

  // Auto-save active cart state
  startAutoSave(getCartState: () => CartState) {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer)
    }

    this.autoSaveTimer = setInterval(() => {
      const state = getCartState()
      if (state.cart.length > 0) {
        this.saveActiveCart(state)
      } else {
        this.clearActiveCart()
      }
    }, this.AUTO_SAVE_INTERVAL)
  }

  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer)
      this.autoSaveTimer = null
    }
  }

  // Active cart persistence
  saveActiveCart(state: CartState) {
    try {
      localStorage.setItem(this.ACTIVE_CART_KEY, JSON.stringify({
        ...state,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.error('Failed to save active cart:', error)
    }
  }

  loadActiveCart(): CartState | null {
    try {
      const saved = localStorage.getItem(this.ACTIVE_CART_KEY)
      if (!saved) return null

      const state = JSON.parse(saved) as CartState
      
      // Check if cart is not too old (older than 24 hours)
      if (Date.now() - state.timestamp > 24 * 60 * 60 * 1000) {
        this.clearActiveCart()
        return null
      }

      return state
    } catch (error) {
      console.error('Failed to load active cart:', error)
      return null
    }
  }

  clearActiveCart() {
    try {
      localStorage.removeItem(this.ACTIVE_CART_KEY)
    } catch (error) {
      console.error('Failed to clear active cart:', error)
    }
  }

  // Suspended sales management
  suspendSale(state: CartState, note?: string, suspendedBy?: string): string {
    try {
      const suspendedSale: SuspendedSale = {
        ...state,
        suspendedAt: Date.now(),
        suspendedBy,
        note,
        id: `suspended_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }

      const existing = this.getSuspendedSales()
      existing.push(suspendedSale)
      
      localStorage.setItem(this.SUSPENDED_SALES_KEY, JSON.stringify(existing))
      
      // Clear active cart since it's now suspended
      this.clearActiveCart()
      
      return suspendedSale.id
    } catch (error) {
      console.error('Failed to suspend sale:', error)
      throw new Error('Failed to suspend sale')
    }
  }

  getSuspendedSales(): SuspendedSale[] {
    try {
      const saved = localStorage.getItem(this.SUSPENDED_SALES_KEY)
      if (!saved) return []

      const sales = JSON.parse(saved) as SuspendedSale[]
      
      // Filter out sales older than 7 days
      const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000)
      const validSales = sales.filter(sale => sale.suspendedAt > weekAgo)
      
      // Save back the filtered list
      if (validSales.length !== sales.length) {
        localStorage.setItem(this.SUSPENDED_SALES_KEY, JSON.stringify(validSales))
      }

      return validSales.sort((a, b) => b.suspendedAt - a.suspendedAt)
    } catch (error) {
      console.error('Failed to load suspended sales:', error)
      return []
    }
  }

  recallSuspendedSale(saleId: string): CartState | null {
    try {
      const suspended = this.getSuspendedSales()
      const sale = suspended.find(s => s.id === saleId)
      
      if (!sale) return null

      // Remove from suspended sales
      const remaining = suspended.filter(s => s.id !== saleId)
      localStorage.setItem(this.SUSPENDED_SALES_KEY, JSON.stringify(remaining))

      // Convert back to cart state
      const { suspendedAt, suspendedBy, note, ...cartState } = sale
      return {
        ...cartState,
        timestamp: Date.now() // Update timestamp
      }
    } catch (error) {
      console.error('Failed to recall suspended sale:', error)
      return null
    }
  }

  deleteSuspendedSale(saleId: string): boolean {
    try {
      const suspended = this.getSuspendedSales()
      const remaining = suspended.filter(s => s.id !== saleId)
      localStorage.setItem(this.SUSPENDED_SALES_KEY, JSON.stringify(remaining))
      return true
    } catch (error) {
      console.error('Failed to delete suspended sale:', error)
      return false
    }
  }

  // Recent sales management
  saveCompletedSale(saleData: {
    billNumber: string
    cart: CartItem[]
    selectedCustomer: Customer | null
    paymentMethod: 'cash' | 'card' | 'upi' | 'split'
    amountReceived: number
    splitPayments?: { cash: string; card: string; upi: string }
    orderDiscount?: { type: 'fixed' | 'percentage'; value: number } | null
    taxRate: number
    subtotal: number
    taxAmount: number
    totalAmount: number
    change: number
  }, completedBy?: string): string {
    try {
      const completedSale: CompletedSale = {
        ...saleData,
        id: `sale_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        completedAt: Date.now(),
        completedBy
      }

      const existing = this.getRecentSales()
      existing.unshift(completedSale) // Add to beginning

      // Keep only the most recent sales
      const trimmed = existing.slice(0, this.MAX_RECENT_SALES)
      
      localStorage.setItem(this.RECENT_SALES_KEY, JSON.stringify(trimmed))
      
      return completedSale.id
    } catch (error) {
      console.error('Failed to save completed sale:', error)
      throw new Error('Failed to save completed sale')
    }
  }

  getRecentSales(): CompletedSale[] {
    try {
      const saved = localStorage.getItem(this.RECENT_SALES_KEY)
      if (!saved) return []

      const sales = JSON.parse(saved) as CompletedSale[]
      
      // Filter out sales older than 30 days
      const monthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
      const validSales = sales.filter(sale => sale.completedAt > monthAgo)
      
      // Save back the filtered list if we removed old sales
      if (validSales.length !== sales.length) {
        localStorage.setItem(this.RECENT_SALES_KEY, JSON.stringify(validSales))
      }

      return validSales.sort((a, b) => b.completedAt - a.completedAt)
    } catch (error) {
      console.error('Failed to load recent sales:', error)
      return []
    }
  }

  getRecentSale(saleId: string): CompletedSale | null {
    try {
      const sales = this.getRecentSales()
      return sales.find(sale => sale.id === saleId) || null
    } catch (error) {
      console.error('Failed to get recent sale:', error)
      return null
    }
  }

  // Navigation protection
  hasActiveCart(): boolean {
    const state = this.loadActiveCart()
    return state !== null && state.cart.length > 0
  }

  // Get summary for confirmation dialogs
  getCartSummary(state: CartState): string {
    const itemCount = state.cart.length
    const totalValue = state.cart.reduce((sum, item) => sum + item.total, 0)
    return `${itemCount} items (₹${totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  }

  // Cleanup old data
  cleanup() {
    try {
      // Clean up suspended sales older than 7 days
      this.getSuspendedSales() // This already cleans up old sales
      
      // Clean up active cart older than 24 hours
      const activeCart = this.loadActiveCart() // This already cleans up old cart
      
      // Clean up recent sales older than 30 days
      this.getRecentSales() // This already cleans up old sales
    } catch (error) {
      console.error('Failed to cleanup cart data:', error)
    }
  }
}

export const cartPersistenceService = new CartPersistenceService()
export type { CartState, SuspendedSale, CompletedSale, CartItem, Customer }